import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { Duration } from 'dayjs/plugin/duration';
import { useWindowSize } from 'hooks/useWindowSize';

import * as S from './styled';
import type { PromoProps } from './types';

const Promo: FC<PromoProps> = ({
  headline,
  subline,
  cover,
  isPreview = false,
  isOwnCollection = false,
  deadline,
  releaseDate,
  isSaleStarted,
  isSaleFinished,
  shareHandle,
  editHandle,
  setIsSaleFinished,
  promoGradient,
  statsContainerRef,
}) => {
  const { t } = useTranslation();
  const [diff, setDiff] = useState<Duration | null>(null);
  const [isInit, setIsInit] = useState(false);
  const [int, setInt] = useState<NodeJS.Timeout | null>(null);

  const [timeTitle, setTimeTitle] = useState<string>(t('collection.promo.finishIn'));

  const width = useWindowSize();
  const getPromoHeadlineSize = (promoHeadline: string) => (promoHeadline?.length > (Math.min(944, width) - 224) / 36 ? 'small' : 'regular');

  const init = () => {
    const targets: { title: string, dayjs: dayjs.Dayjs }[] = [];

    if (!isSaleStarted && releaseDate) {
      targets.push({ title: t('collection.promo.startIn'), dayjs: dayjs(releaseDate) });
    }
    if (!isSaleFinished && deadline) {
      targets.push({ title: t('collection.promo.finishIn'), dayjs: dayjs(deadline) });
    }

    // let interval: ReturnType<typeof setTimeout>;

    const onTimer = () => {
      if (targets.length) {
        const newDuration = dayjs.duration(targets[0].dayjs.diff(dayjs()));

        if (newDuration.asSeconds() <= 0) {
          targets.shift();

          if (targets.length) {
            setTimeTitle(targets[0].title);
            setDiff(dayjs.duration(targets[0].dayjs.diff(dayjs())));
          } else {
            setDiff(null);
            if (int) {
              clearInterval(int);
            }

            if (setIsSaleFinished) setIsSaleFinished(true);
          }
        } else {
          setDiff(newDuration);
        }
      }
    };

    if (targets.length) {
      setTimeTitle(targets[0].title);
      setInt(setInterval(onTimer, 1000));
      onTimer();
    }
    setIsInit(true);
  };

  if (!isInit) init();

  useEffect(() => () => {
    if (int) {
      clearInterval(int);
    }
  }, []);

  useEffect(() => {
    if (int) {
      clearInterval(int);
    }
    init();
  }, [isSaleStarted, isSaleStarted, deadline, releaseDate]);

  const diffData = diff && [
    [`0${diff.get('days').toString()}`.slice(-2), t('common.intervals.days')],
    [`0${diff.get('hours').toString()}`.slice(-2), t('common.intervals.hours')],
    [`0${diff.get('minutes').toString()}`.slice(-2), t('common.intervals.minutes')],
    [`0${diff.get('seconds').toString()}`.slice(-2), t('common.intervals.seconds')],
  ];

  return (
    <S.PromoWrapper data-test-id="promo_wrapper" $hasTimer={Boolean(diffData)}>
      {cover && (
        <S.PromoCover
          data-test-id="promo_cover"
          $hasTimer={Boolean(diffData)}
          media={cover}
          gradient={promoGradient}
          lazy
        />
      )}
      {!isPreview && (
        <S.ButtonsWrapper>
          {isOwnCollection && (
            <S.PromoButton
              data-test-id="promo_edit_button"
              background="lightTransparent"
              $align="left"
              onClick={editHandle}
            >
              {t('common.edit')}
            </S.PromoButton>
          )}
          <S.PromoButton
            data-test-id="promo_share_button"
            background="lightTransparent"
            onClick={shareHandle}
            $align="right"
          >
            {t('common.share')}
          </S.PromoButton>
        </S.ButtonsWrapper>
      )}
      <S.PromoContent>
        <S.PromoHeadlineWrapper $isDeadline={!!deadline}>
          <S.PromoHeadline data-test-id="promo_headline" $size={getPromoHeadlineSize(headline)}>
            {headline}
          </S.PromoHeadline>
          <S.PromoSubline data-test-id="promo_subline">{subline}</S.PromoSubline>
        </S.PromoHeadlineWrapper>
        {!isSaleFinished && diffData && (
          <S.PromoCountdownWrapper data-test-id="promo_countdown_wrapper">
            <S.PromoCountdownHeadline data-test-id="promo_countdown_headline">{timeTitle}</S.PromoCountdownHeadline>
            <S.PromoCountdownNumberRow data-test-id="promo_countdown_number_row">
              {diffData.map((item) => (
                <div key={item[1]}>
                  <S.PromoCountdownNumberBox data-test-id="promo_countdown_number_box">{item[0]}</S.PromoCountdownNumberBox>
                  <S.PromoCountdownNumberLabel data-test-id={`promo_countdown_${item[1]}_label`}>{item[1]}</S.PromoCountdownNumberLabel>
                </div>
              ))}
            </S.PromoCountdownNumberRow>
          </S.PromoCountdownWrapper>
        )}
        {isSaleFinished && <div ref={statsContainerRef} />}
      </S.PromoContent>
    </S.PromoWrapper>
  );
};

export default Promo;
